import { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Plus, Edit, Trash2, GripVertical, BarChart3 } from 'lucide-react';
import { BRAND_COLORS, BRAND_LABELS, API } from '@/constants/bluemonk';

export const AdminProgressSection = ({ items, headers, onRefresh }) => {
  const [newItem, setNewItem] = useState({ title: '', brand: 'all', status: 'not-started' });
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});

  const statusOptions = [
    { value: 'not-started', label: 'Not Started' },
    { value: 'in-progress', label: 'In Progress' },
    { value: 'review', label: 'In Review' },
    { value: 'completed', label: 'Completed' },
    { value: 'blocked', label: 'Blocked' },
  ];

  const handleAdd = async () => {
    if (!newItem.title.trim()) {
      toast.error('Please enter a title');
      return;
    }
    try {
      await axios.post(`${API}/progress`, {
        ...newItem,
        order: items.length
      }, { headers });
      toast.success('Progress item added');
      setNewItem({ title: '', brand: 'all', status: 'not-started' });
      onRefresh();
    } catch (error) {
      toast.error('Failed to add');
    }
  };

  const handleUpdate = async (id) => {
    try {
      await axios.put(`${API}/progress/${id}`, editData, { headers });
      toast.success('Updated');
      setEditingId(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this progress item?')) return;
    try {
      await axios.delete(`${API}/progress/${id}`, { headers });
      toast.success('Deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const startEdit = (item) => {
    setEditingId(item.id);
    setEditData({ title: item.title, brand: item.brand, status: item.status });
  };

  const brandGroups = {};
  items.forEach(item => {
    if (!brandGroups[item.brand]) {
      brandGroups[item.brand] = [];
    }
    brandGroups[item.brand].push(item);
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-900">Progress Tracker</h1>
        <p className="text-sm text-slate-500">Track deliverables progress per brand</p>
      </div>

      <Card className="p-4">
        <h2 className="font-semibold mb-4">Add Progress Item</h2>
        <div className="grid grid-cols-4 gap-4">
          <Input
            placeholder="Deliverable title"
            value={newItem.title}
            onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
            className="col-span-2"
          />
          <Select value={newItem.brand} onValueChange={(v) => setNewItem({ ...newItem, brand: v })}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Brands</SelectItem>
              <SelectItem value="renew-circuits">Renew Circuits</SelectItem>
              <SelectItem value="bluemonk-group">BlueMonk Group</SelectItem>
              <SelectItem value="deshwal">Deshwal</SelectItem>
            </SelectContent>
          </Select>
          <Button onClick={handleAdd}>
            <Plus className="h-4 w-4 mr-2" />
            Add
          </Button>
        </div>
      </Card>

      {Object.keys(brandGroups).length === 0 ? (
        <Card className="p-8 text-center">
          <BarChart3 className="h-12 w-12 text-slate-400 mx-auto mb-4" />
          <p className="text-lg font-medium text-slate-700">No progress items yet</p>
          <p className="text-slate-500">Add items to track project progress</p>
        </Card>
      ) : (
        Object.entries(brandGroups).map(([brand, brandItems]) => {
          const colors = BRAND_COLORS[brand];
          const completed = brandItems.filter(i => i.status === 'completed').length;
          const progress = Math.round((completed / brandItems.length) * 100);
          
          return (
            <Card key={brand} className={`overflow-hidden border-l-4 ${colors.border}`}>
              <div className={`p-4 ${colors.bg} border-b flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <Badge className={`${colors.badge} text-white`}>
                    {BRAND_LABELS[brand]}
                  </Badge>
                  <span className="font-semibold text-slate-700">
                    {brand === 'renew-circuits' ? 'Renew Circuits' :
                     brand === 'bluemonk-group' ? 'BlueMonk Group' :
                     brand === 'deshwal' ? 'Deshwal' : 'All Brands'}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-24 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${colors.badge}`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-slate-600">{progress}%</span>
                </div>
              </div>
              <div className="p-4 space-y-2">
                {brandItems.sort((a, b) => (a.order || 0) - (b.order || 0)).map((item) => (
                  <div key={item.id} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    {editingId === item.id ? (
                      <div className="flex-1 flex items-center gap-2">
                        <Input
                          value={editData.title}
                          onChange={(e) => setEditData({ ...editData, title: e.target.value })}
                          className="flex-1"
                        />
                        <Select value={editData.status} onValueChange={(v) => setEditData({ ...editData, status: v })}>
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {statusOptions.map(opt => (
                              <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button size="sm" onClick={() => handleUpdate(item.id)}>Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditingId(null)}>Cancel</Button>
                      </div>
                    ) : (
                      <>
                        <div className="flex items-center gap-3">
                          <GripVertical className="h-4 w-4 text-slate-300" />
                          <span className={`font-medium ${item.status === 'completed' ? 'text-green-700 line-through' : ''}`}>
                            {item.title}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Select value={item.status} onValueChange={async (v) => {
                            try {
                              await axios.put(`${API}/progress/${item.id}`, { status: v }, { headers });
                              onRefresh();
                            } catch (e) { toast.error('Failed'); }
                          }}>
                            <SelectTrigger className="w-32 h-8 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {statusOptions.map(opt => (
                                <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Button size="sm" variant="ghost" onClick={() => startEdit(item)}>
                            <Edit className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="ghost" className="text-red-600" onClick={() => handleDelete(item.id)}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </Card>
          );
        })
      )}
    </div>
  );
};
